import { useTranslation } from 'react-i18next';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { DarwinFlex, DarwinSpin } from 'components/darwin-ui';
import { Column, Pie } from '@ant-design/charts';
import DarwinTypography from 'components/darwin-ui/Typography';
import styles from './index.module.css';
import { useUI } from 'contexts/UiContext';
import {
  useGetLocationTransactions,
  useGetProcessTransactions,
} from 'api/services/dashboardService';

export const Dashboard = () => {
  const { t } = useTranslation();
  const { selectedCompany } = useUI();

  const {
    data: locationTransactionsData,
    isLoading: isLoadingLocationTransactions,
    isFetching: isFetchingLocationTransactions,
    isPending: isPendingLocationTransactions,
  } = useGetLocationTransactions(selectedCompany || '');

  const {
    data: processTransactionsData,
    isLoading: isLoadingProcessTransactions,
    isFetching: isFetchingProcessTransactions,
    isPending: isPendingProcessTransactions,
  } = useGetProcessTransactions(selectedCompany || '');

  const isLoadingLocationData =
    isLoadingLocationTransactions ||
    isFetchingLocationTransactions ||
    isPendingLocationTransactions;

  const isLoadingProcessData =
    isLoadingProcessTransactions ||
    isFetchingProcessTransactions ||
    isPendingProcessTransactions;

  const locationData = locationTransactionsData?.data || [];
  const processData = processTransactionsData?.data || [];

  const config = {
    data: locationData,
    xField: 'location',
    yField: 'transactions',
    xAxis: {
      title: {
        visible: true,
        text: t('dashboard.location-axis'),
      },
      label: {
        autoRotate: false,
      },
    },
    yAxis: {
      title: {
        visible: true,
        text: t('dashboard.transaction-count-axis'),
      },
    },
    style: {
      fill: '#ee6002',
      radiusTopLeft: 10,
      radiusTopRight: 10,
    },
    tooltip: (item: any) => {
      return { origin: item };
    },
    interaction: {
      tooltip: {
        render: (_e: any, { title, items }: any) => {
          if (!items || items.length === 0) return null;
          const item = items[0];
          const { origin } = item;
          return (
            <div className={styles.tooltipContainer}>
              <div className={styles.tooltipRow}>
                <strong>{t('dashboard.location-axis')}:</strong>{' '}
                {origin?.location || title}
              </div>
              <div>
                <strong>{t('dashboard.transaction-count-axis')}:</strong>{' '}
                {origin?.transactions || item.value}
              </div>
            </div>
          );
        },
      },
    },
    meta: {
      location: {
        alias: t('dashboard.location-axis'),
      },
      transactions: {
        alias: t('dashboard.transaction-count-axis'),
      },
    },
  };

  const pieConfig = {
    data: processData,
    angleField: 'transactions',
    colorField: 'process',
    radius: 0.9,
    innerRadius: 0.6,
    tooltip: (item: any) => {
      return { origin: item };
    },
    interaction: {
      tooltip: {
        render: (_e: any, { title, items }: any) => {
          if (!items || items.length === 0) return null;
          const item = items[0];
          const { origin } = item;

          return (
            <div className={styles.tooltipContainer}>
              <div className={styles.tooltipRow}>
                <strong>{t('process')}:</strong> {origin?.process || title}
              </div>
              <div>
                <strong>{t('dashboard.transaction-count-axis')}:</strong>{' '}
                {origin?.transactions ?? item.value}
              </div>
            </div>
          );
        },
      },
    },
    label: {
      offset: '-30%',
      content: ({ percent }: { percent: number }) =>
        `${(percent * 100).toFixed(0)}%`,
      style: {
        fontSize: 12,
        textAlign: 'center' as const,
      },
    },
    legend: {
      position: 'right' as const,
    },
  };

  return (
    <>
      <ContentHeader title={t('dashboard')} />
      <DarwinFlex vertical className={styles.dashboardContainer}>
        <section>
          <DarwinTypography.Title level={4}>
            {t('dashboard.activity-distribution-title')}
          </DarwinTypography.Title>
          <div className={styles.chartContainer}>
            {isLoadingLocationData ? (
              <DarwinSpin />
            ) : locationData.length > 0 ? (
              <Column {...config} />
            ) : null}
          </div>
        </section>

        <section>
          <DarwinTypography.Title level={4}>
            {t('dashboard.transactions-per-process-title')}
          </DarwinTypography.Title>
          <div className={styles.chartContainer}>
            {isLoadingProcessData ? (
              <DarwinSpin />
            ) : processData.length > 0 ? (
              <Pie {...pieConfig} />
            ) : null}
          </div>
        </section>
      </DarwinFlex>
    </>
  );
};
